// Super-admin dashboard — the landing page after login. Loads one
// /admin/dashboard/summary request and renders 6 preview widgets:
//
//   1. Top-line KPIs (users / paid / MRR / ARR / signups this month)
//   2. Pending Invoices       -> /invoices/pending
//   3. Pending Verifications  -> /users/pending-verifications
//   4. Inactive Users         -> /users?status=inactive
//   5. User Chatbots          -> /users/by-chatbots
//   6. Chatbot History        -> /chatbot-history (user / chatbot tabs)
//
// Each widget shows top-5 preview rows + a "View all" CTA so the admin sees
// the actionable items without a per-widget fetch.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, CardActionArea, Button, Chip, Stack,
    Divider, CircularProgress, Alert, Grid, IconButton, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import axiosInstance from '../utils/axiosInstance';
import { formatCurrency, formatDate } from '../utils/format';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const Dashboard = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/admin/dashboard/summary');
            setSummary(res.data || null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load dashboard.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { document.title = 'Dashboard | Klonit Super Admin'; }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) {
        return <Alert severity="error" sx={{ fontFamily }}>{error}</Alert>;
    }
    if (!summary) return null;

    const topline = summary.topline || {};
    return (
        <Box sx={{ fontFamily }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontSize: '1.4rem', fontWeight: 800, color: '#0f172a' }}>
                        Dashboard
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                        What needs attention today and how the platform is performing.
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton size="small" onClick={load}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Top-line KPI strip */}
            <KpiStrip topline={topline} />

            {/* Action-oriented widgets (things needing attention) */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={6} lg={4}>
                    <PendingInvoicesCard
                        data={summary.pending_invoices}
                        onOpenAll={() => navigate('/invoices/pending')}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <PendingVerificationsCard
                        data={summary.pending_verifications}
                        onOpenAll={() => navigate('/users/pending-verifications')}
                    />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                    <InactiveUsersCard
                        data={summary.inactive_users}
                        onOpenAll={() => navigate('/users?status=inactive')}
                    />
                </Grid>
            </Grid>

            {/* Platform-usage widgets (chatbots) */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12} lg={6}>
                    <UserChatbotsCard
                        data={summary.user_chatbots}
                        onOpenAll={() => navigate('/users/by-chatbots')}
                        onOpenUser={(uid) => navigate(`/users/${uid}`)}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <ChatHistoryCard
                        byUser={summary.chatbot_history_by_user}
                        byChatbot={summary.chatbot_history_by_chatbot}
                        onOpenAll={() => navigate('/chatbot-history')}
                        onOpenUser={(uid) => navigate(`/users/${uid}`)}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

// --- Top-line KPI strip -----------------------------------------------------
const KpiStrip = ({ topline }) => (
    <Card variant="outlined" sx={{ borderRadius: '14px' }}>
        <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                <Box sx={{
                    width: 32, height: 32, borderRadius: '10px',
                    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <TrendingUpIcon sx={{ color: '#fff', fontSize: '1.05rem' }} />
                </Box>
                <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a' }}>
                    Platform overview
                </Typography>
            </Stack>
            <Grid container spacing={1.5}>
                <Kpi label="Total users" value={topline.total_users} />
                <Kpi label="Active users" value={topline.active_users} />
                <Kpi label="Paid subscribers" value={topline.paid_subscribers} accent="#059669" />
                <Kpi label="Freemium" value={topline.freemium_subscribers} />
                <Kpi label="MRR" value={formatCurrency(topline.mrr || 0, 'usd')} accent="#3b82f6" />
                <Kpi label="ARR" value={formatCurrency(topline.arr || 0, 'usd')} accent="#3b82f6" />
                <Kpi label="Signups this month" value={topline.signups_this_month} />
                <Kpi label="Unverified" value={topline.unverified_users}
                     accent={(topline.unverified_users || 0) > 0 ? '#d97706' : undefined} />
            </Grid>
        </CardContent>
    </Card>
);

const Kpi = ({ label, value, accent }) => (
    <Grid item xs={6} sm={4} md={3} lg={1.5}>
        <Box sx={{
            p: 1, borderRadius: '10px', border: '1px solid #e2e8f0',
            bgcolor: 'rgba(248, 250, 252, 0.6)',
        }}>
            <Typography sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 700,
                              textTransform: 'uppercase', color: '#94a3b8', letterSpacing: 0.5 }}>
                {label}
            </Typography>
            <Typography sx={{ fontFamily, fontSize: '1.05rem', fontWeight: 800,
                              color: accent || '#0f172a', mt: 0.25 }}>
                {value ?? '--'}
            </Typography>
        </Box>
    </Grid>
);

// --- Reusable widget shell --------------------------------------------------
const WidgetCard = ({ icon, title, accentColor, count, suffix, children, onOpenAll, emptyHint }) => (
    <Card variant="outlined" sx={{ borderRadius: '14px', height: '100%',
                                    display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '10px',
                        backgroundColor: `${accentColor}15`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', color: accentColor,
                    }}>
                        {icon}
                    </Box>
                    <Box>
                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                            {title}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                            {count ?? 0}{suffix ? ` ${suffix}` : ''}
                        </Typography>
                    </Box>
                </Stack>
                <Button
                    size="small" endIcon={<ArrowForwardIcon fontSize="small" />}
                    onClick={onOpenAll}
                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                >
                    View all
                </Button>
            </Stack>
            <Divider sx={{ mb: 1.25 }} />
            <Box sx={{ flex: 1, minHeight: 120 }}>
                {(count ?? 0) === 0
                    ? <EmptyState message={emptyHint || 'Nothing here right now.'} />
                    : children}
            </Box>
        </CardContent>
    </Card>
);

const EmptyState = ({ message }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
               justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
        <Typography sx={{ fontFamily, fontSize: '0.85rem' }}>{message}</Typography>
    </Box>
);

// --- Individual widget cards ------------------------------------------------
const PendingInvoicesCard = ({ data, onOpenAll }) => (
    <WidgetCard
        icon={<ReceiptLongIcon fontSize="small" />} title="Pending Invoices"
        accentColor="#d97706" count={data?.count} suffix="unpaid"
        onOpenAll={onOpenAll} emptyHint="All invoices settled."
    >
        <Stack spacing={0.75}>
            {(data?.items || []).map((inv) => (
                <Stack key={inv.id} direction="row" alignItems="center" justifyContent="space-between"
                       sx={{ py: 0.5 }}>
                    <Box sx={{ overflow: 'hidden' }}>
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', fontWeight: 600, color: '#1e293b' }}
                                    noWrap>
                            {inv.invoice_number}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#64748b' }} noWrap>
                            {inv.billed_to_email || `#${inv.team_owner_id}`} · {formatDate(inv.issued_at)}
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 700, color: '#d97706', ml: 1 }}>
                        {formatCurrency(inv.total, inv.currency)}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    </WidgetCard>
);

const PendingVerificationsCard = ({ data, onOpenAll }) => (
    <WidgetCard
        icon={<MarkEmailUnreadIcon fontSize="small" />} title="Pending Verifications"
        accentColor="#7c3aed" count={data?.count} suffix="awaiting email confirm"
        onOpenAll={onOpenAll} emptyHint="No unverified users."
    >
        <Stack spacing={0.5}>
            {(data?.items || []).map((u) => (
                <Stack key={u.user_id} direction="row" alignItems="center" justifyContent="space-between"
                       sx={{ py: 0.4 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#1e293b' }} noWrap>
                        {u.user_email}
                    </Typography>
                    <Chip size="small" label={u.account_type || 'individual'} sx={{ fontFamily }} />
                </Stack>
            ))}
        </Stack>
    </WidgetCard>
);

const InactiveUsersCard = ({ data, onOpenAll }) => (
    <WidgetCard
        icon={<PersonOffIcon fontSize="small" />} title="Inactive Users"
        accentColor="#dc2626" count={data?.count} suffix="deactivated"
        onOpenAll={onOpenAll} emptyHint="No inactive accounts."
    >
        <Stack spacing={0.5}>
            {(data?.items || []).map((u) => (
                <Stack key={u.user_id} direction="row" alignItems="center" justifyContent="space-between"
                       sx={{ py: 0.4 }}>
                    <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#1e293b' }} noWrap>
                        {u.user_email}
                    </Typography>
                    <Chip size="small" color="error" variant="outlined" label="Off" sx={{ fontFamily }} />
                </Stack>
            ))}
        </Stack>
    </WidgetCard>
);

const UserChatbotsCard = ({ data, onOpenAll, onOpenUser }) => (
    <WidgetCard
        icon={<SmartToyIcon fontSize="small" />} title="User Chatbots"
        accentColor="#0ea5e9" count={data?.total_chatbots} suffix="chatbots in total"
        onOpenAll={onOpenAll} emptyHint="No chatbots created yet."
    >
        <Stack spacing={0.5}>
            {(data?.items || []).map((row) => (
                <CardActionArea key={row.user_id} onClick={() => onOpenUser?.(row.user_id)}
                                sx={{ borderRadius: '8px', px: 0.75, py: 0.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#1e293b' }} noWrap>
                            {row.user_email}
                        </Typography>
                        <Chip size="small" label={`${row.chatbot_count} bot${row.chatbot_count !== 1 ? 's' : ''}`}
                              sx={{ fontFamily, fontWeight: 600 }} />
                    </Stack>
                </CardActionArea>
            ))}
        </Stack>
    </WidgetCard>
);

// Chat-history widget shows BOTH cuts (user-wise + chatbot-wise) side-by-side
// since they answer different questions ("who is using it most?" vs
// "which deployed chatbot is generating the most traffic?").
const ChatHistoryCard = ({ byUser, byChatbot, onOpenAll, onOpenUser }) => {
    const totalMessages = (byUser?.total_messages || 0);
    return (
        <Card variant="outlined" sx={{ borderRadius: '14px', height: '100%',
                                        display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Box sx={{
                            width: 32, height: 32, borderRadius: '10px',
                            backgroundColor: 'rgba(16, 185, 129, 0.12)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981',
                        }}>
                            <ChatBubbleOutlineIcon fontSize="small" />
                        </Box>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                                Chatbot History
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.72rem', color: '#94a3b8' }}>
                                {totalMessages} messages tracked
                            </Typography>
                        </Box>
                    </Stack>
                    <Button
                        size="small" endIcon={<ArrowForwardIcon fontSize="small" />}
                        onClick={onOpenAll}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                    >
                        View all
                    </Button>
                </Stack>
                <Divider sx={{ mb: 1.25 }} />
                <Grid container spacing={1.5} sx={{ flex: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700,
                                          textTransform: 'uppercase', color: '#64748b',
                                          letterSpacing: 0.5, mb: 0.75 }}>
                            By user (top 5)
                        </Typography>
                        {(byUser?.items || []).length === 0 ? (
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#94a3b8' }}>
                                No activity yet.
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {byUser.items.map((row) => (
                                    <CardActionArea key={row.user_id} onClick={() => onOpenUser?.(row.user_id)}
                                                    sx={{ borderRadius: '8px', px: 0.75, py: 0.5 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#1e293b' }} noWrap>
                                                {row.user_email}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 700, color: '#10b981' }}>
                                                {row.message_count}
                                            </Typography>
                                        </Stack>
                                    </CardActionArea>
                                ))}
                            </Stack>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 700,
                                          textTransform: 'uppercase', color: '#64748b',
                                          letterSpacing: 0.5, mb: 0.75 }}>
                            By chatbot (top 5)
                        </Typography>
                        {(byChatbot?.items || []).length === 0 ? (
                            <Typography sx={{ fontFamily, fontSize: '0.82rem', color: '#94a3b8' }}>
                                No embed traffic yet.
                            </Typography>
                        ) : (
                            <Stack spacing={0.5}>
                                {byChatbot.items.map((row) => (
                                    <Stack key={row.chatbot_id} direction="row" alignItems="center"
                                           justifyContent="space-between" sx={{ py: 0.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#1e293b' }} noWrap>
                                            {row.chatbot_name}
                                        </Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.78rem', fontWeight: 700, color: '#10b981' }}>
                                            {row.message_count}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

export default Dashboard;
